import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * Post-Stripe-Checkout landing page. Stripe redirects here on a successful
 * payment with `acceptance_id` (we set it) and `session_id` (Stripe sets it).
 *
 * We poll /api/acceptance/{id} every 1.5s until the backend reports
 * stripe_payment_status === "paid" (the backend refreshes from Stripe on
 * each GET so we don't need a webhook for the happy path).
 */
export default function AcceptanceSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const acceptanceId =
    params.get("acceptance_id") ||
    localStorage.getItem("jante_pending_acceptance") ||
    null;

  const [record, setRecord] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!acceptanceId) return;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const r = await api.get(`/acceptance/${acceptanceId}`);
        if (cancelled) return;
        // Only update state when the response actually represents progress —
        // this prevents the page from re-rendering (and flashing the
        // "Confirming…" spinner) every 1.5s when nothing has changed.
        setRecord((prev) => {
          const same =
            prev &&
            prev.id === r.data.id &&
            prev.status === r.data.status &&
            prev.stripe_payment_status === r.data.stripe_payment_status;
          return same ? prev : r.data;
        });
        if (r.data.stripe_payment_status !== "paid" && attempts < 20) {
          timer = setTimeout(() => setAttempts((a) => a + 1), 1500);
        } else if (r.data.stripe_payment_status === "paid") {
          localStorage.removeItem("jante_pending_acceptance");
        }
      } catch {
        if (!cancelled && attempts < 20) {
          timer = setTimeout(() => setAttempts((a) => a + 1), 1500);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptanceId, attempts]);

  if (!acceptanceId) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="acceptance-no-id">
        <h1 className="font-display text-3xl text-[var(--emerald)]">
          We can't find this acceptance.
        </h1>
        <button
          onClick={() => navigate("/")}
          className="btn-primary mt-6"
          data-testid="acceptance-home-btn"
        >
          Back to Sasha
        </button>
      </div>
    );
  }

  const paid = record?.stripe_payment_status === "paid";
  const isHubSpot = (record?.payment_method || "").toLowerCase() === "hubspot";
  // For HubSpot, the user returns from HubSpot's confirmation page — we treat
  // the acceptance itself as the success state because the deposit confirmation
  // happens out-of-band (concierge confirms via /admin). Skip the spinner.
  const confirmed = paid || isHubSpot;

  return (
    <div className="max-w-lg mx-auto px-6 py-16" data-testid="acceptance-success-page">
      {confirmed ? (
        <>
          <div className="overline flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} className="text-[var(--gold)]" /> {isHubSpot && !paid ? "Acceptance recorded" : "Deposit confirmed"}
          </div>
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-3 leading-tight">
            {isHubSpot && !paid ? "Your trip is on the desk." : "Your trip is locked."}
          </h1>
          <p className="text-base text-[var(--ink-2)] mt-5 leading-relaxed">
            {isHubSpot && !paid ? (
              <>
                Beautiful. We've logged your acceptance of the{" "}
                <strong>${(record.deposit_amount_usd || 0).toLocaleString()}</strong> deposit. The
                moment HubSpot Payments confirms the transaction, your dedicated concierge will
                begin coordinating flights, accommodations, and the curated extras you selected
                — usually within an hour during business hours.
              </>
            ) : (
              <>
                Beautiful. Your <strong>${(record.deposit_amount_usd || 0).toLocaleString()}</strong> deposit
                has been received. Sasha and your dedicated concierge are now
                coordinating each leg — flights, accommodations, and the curated
                extras you selected.
              </>
            )}
          </p>

          <div className="lux-card-dark !p-6 mt-7">
            <div className="overline text-[var(--gold)]">Acceptance reference</div>
            <div
              className="font-display text-xl mt-1 tabular-nums break-all"
              data-testid="acceptance-success-id"
            >
              {record.id}
            </div>
            {record.trip_ref && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ivory)]/60 mt-2">
                Trip ref · {record.trip_ref}
              </div>
            )}
          </div>

          <div className="mt-8 p-5 bg-[var(--brand-red)]/8 border-l-2 border-[var(--brand-red)] text-sm text-[var(--ink-2)] leading-relaxed">
            <strong className="text-[var(--brand-red)]">What happens next.</strong>{" "}
            Within 24 hours, you'll receive your fully detailed itinerary
            with every confirmation number from our concierge team. If
            anything is time-sensitive, we'll call you on the number we
            already have on file.
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/portal")}
              className="btn-primary"
              data-testid="acceptance-go-portal"
            >
              View trip in portal <ArrowRight size={14} strokeWidth={1.5} />
            </button>
            <button
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] px-3 py-2 self-center"
              data-testid="acceptance-continue-sasha"
            >
              Continue with Sasha
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-12" data-testid="acceptance-pending">
          <Loader2
            size={28}
            className="animate-spin text-[var(--gold)] mx-auto"
          />
          <h1 className="font-display text-3xl text-[var(--emerald)] mt-5">
            Confirming your deposit…
          </h1>
          <p className="text-sm text-[var(--ink-2)] mt-3">
            {isHubSpot
              ? "HubSpot Payments is finalising the transaction. This usually takes a few seconds — or just close this tab and we'll email you the confirmation."
              : "Stripe is finalising the transaction. This usually takes a few seconds."}
          </p>
          {attempts >= 20 && (
            <p className="text-xs text-[var(--brand-red)] mt-6 leading-relaxed">
              Taking longer than expected? Your payment may have completed but
              hasn't synced yet. Your reference is{" "}
              <strong className="break-all">{acceptanceId}</strong>. Janté
              concierge has been notified.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
